import { CloseFilter, ShowFilter } from "assets/icons";
import React, { useEffect, useState } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { ThemeProvider, createTheme, styled } from "@mui/material/styles";
import {
  circularProgressWrapperStyles,
  inputLabel,
  participantListTableCell,
  selectCategory,
  showFilterButton,
} from "entities/admin/lib/MUIStyles/MUIStyles";
import {
  useAppDispatch,
  useAppSelector,
  useDebounce,
} from "shared/lib/hooks/hooks";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { getMarathonResultsByAdmin } from "entities/admin/model/thunk/getMarathonResultsByAdmin";
import { getMarathonResultsBySearch } from "../../../entities/admin/model/thunk/getMarathonResultsBySearch";
import searchIcon from "assets/images/admin/search-icon.svg";
import styles from "../../../entities/admin/ui/MarathonResults/marathonResults.module.scss";
import { useTranslation } from "react-i18next";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#ECF2F7",
    color: "#3E444A",
    padding: "8px",
    fontFamily: "Raleway",
    fontSize: "12px",
    lineHeight: "20px",
    fontWeight: "700 !important",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#F5FAFF",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0090D1",
          },
        },
      },
    },
  },
});

const AccessibleToAllMarathonResults: React.FC = (): JSX.Element => {
  const id = 1;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentLanguage = localStorage.getItem("i18nextLng");

  const isGetMarathonResultsLoading = useAppSelector(
    (state) => state.marathon.isGetMarathonResultsLoading
  );
  const marathonResults = useAppSelector(
    (state) => state.marathon.marathonResults
  );
  const marathonResultsCount = useAppSelector(
    (state) => state.marathon.marathonResultsCount
  );
  const marathonResultsDistances = useAppSelector(
    (state) => state.marathon.marathonResultsDistances
  );
  const isGetMarathonResultsBySearchLoading = useAppSelector(
    (state) => state.marathon.isGetMarathonResultsBySearchLoading
  );

  const [isShowFilter, setIsShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [participantSearch, setParticipantSearch] = useState<string>("");
  const [distance, setDistance] = useState("");
  const [gender, setGender] = useState("");
  const debouncedValue = useDebounce<string>(participantSearch, 800);
  const [distanceAges, setDistanceAges] = useState<any>([]);
  const [chosenDistanceAge, setChosenDistanceAge] = useState<string>("");

  const handleOnPageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setPage(page);
  };

  const handleParticipantSearch = (participant: string) => {
    setParticipantSearch(participant);
    setPage(1);
  };

  useEffect(() => {
    if (id) {
      dispatch(
        getMarathonResultsByAdmin({
          marathonId: +id,
          page,
          pageSize,
          gender,
          distance,
          distanceAgeId: chosenDistanceAge,
        })
      );
    }
  }, [
    dispatch,
    id,
    page,
    pageSize,
    distance,
    gender,
    chosenDistanceAge,
    currentLanguage,
  ]);

  useEffect(() => {
    if (id) {
      dispatch(
        getMarathonResultsBySearch({
          marathonId: +id,
          participantSearch: debouncedValue,
        })
      );
    }
  }, [dispatch, debouncedValue, id, currentLanguage]);

  useEffect(() => {
    setDistance("");
    setChosenDistanceAge("");
    setGender("");
    setParticipantSearch("");
  }, [id, currentLanguage]);

  useEffect(() => {
    const distanceAges = marathonResultsDistances.find(
      (marathonResultDistance) => marathonResultDistance.name === distance
    );

    if (distanceAges) {
      setDistanceAges(distanceAges.distanceAges);
    }

    if (distanceAges && gender) {
      const filteredDistanceAgesByGender = distanceAges.distanceAges.filter(
        (distanceAges: any) => {
          if (gender === "false") {
            return distanceAges.gender === false;
          }

          return distanceAges.gender === true;
        }
      );

      setChosenDistanceAge("");
      setDistanceAges(filteredDistanceAgesByGender);
    }
  }, [distance, marathonResultsDistances, gender]);

  useEffect(() => {
    if (!isShowFilter) {
      setGender("");
      setChosenDistanceAge("");
      setDistance("");
    }
  }, [isShowFilter]);

  return (
    <>
      <div className={styles.marathonResultsTop}>
        <h1 className={styles.marathonResultsTopMainTitle}>
          Иссык-Куль ШОС марафон Run the Silk Road 2023
        </h1>
      </div>
      <div className={styles.marathonResultsContainer}>
        <div className={styles.marathonResultsInnerTop}>
          <h2 className={styles.marathonResultsSubTitle}>
            {t("marathonResults.participantsList")}{" "}
            <span className={styles.marathonResultsCount}>
              {marathonResultsCount}
            </span>
          </h2>
        </div>
        {marathonResultsCount && marathonResultsCount > 0 ? (
          <span className={styles.participantsFilterContainer}>
            <span className={styles.participantInputContainer}>
              <input
                className={styles.participantInput}
                placeholder={t("marathonResults.searchPlaceholder")}
                type="text"
                value={participantSearch}
                onChange={(e) => handleParticipantSearch(e.target.value)}
              />
              <img
                onClick={() => {
                  dispatch(
                    getMarathonResultsBySearch({
                      marathonId: +id,
                      participantSearch: debouncedValue,
                    })
                  );
                }}
                className={styles.participantSearchIcon}
                src={searchIcon}
                alt="Поиск"
              />
            </span>
            <Button
              startIcon={isShowFilter ? <CloseFilter /> : <ShowFilter />}
              variant="contained"
              sx={showFilterButton}
              onClick={() => setIsShowFilter((prevState) => !prevState)}
            >
              {isShowFilter
                ? t("marathonResults.hideFilters")
                : t("marathonResults.showFilters")}
            </Button>
          </span>
        ) : null}
        {isShowFilter ? (
          <ThemeProvider theme={theme}>
            <Box
              sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
            >
              <div className={styles.filterDistance}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span className={styles.selectTitle}>
                    {t("marathonResults.distance")}
                  </span>
                  <button
                    className={styles.resetSelectedItem}
                    type="button"
                    onClick={() => {
                      setDistance("");
                      setChosenDistanceAge("");
                      setGender("");
                    }}
                  >
                    {t("marathonResults.resetFilter")}
                  </button>
                </div>
                <FormControl fullWidth>
                  {distance ? null : (
                    <InputLabel
                      id="demo-simple-select-label"
                      sx={inputLabel}
                      shrink={false}
                    >
                      {t("marathonResults.chooseDistance")}
                    </InputLabel>
                  )}
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={distance}
                    onChange={(event) => setDistance(event.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "12px",
                          padding: "0 10px",
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "#e3f2fd",
                          },
                        },
                      },
                    }}
                    sx={selectCategory}
                  >
                    {marathonResultsDistances.map((marathonResultDistance) => (
                      <MenuItem
                        key={marathonResultDistance.id}
                        value={marathonResultDistance.name}
                        sx={{ borderRadius: "10px" }}
                        className={styles.resetMuiDefaultStyle}
                      >
                        {marathonResultDistance.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.genderFilter}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span className={styles.selectTitle}>
                    {t("marathonResults.gender")}
                  </span>
                  <button
                    className={styles.resetSelectedItem}
                    type="button"
                    onClick={() => setGender("")}
                  >
                    {t("marathonResults.resetFilter")}
                  </button>
                </div>
                <FormControl fullWidth>
                  {gender ? null : (
                    <InputLabel
                      id="demo-simple-select-label"
                      sx={inputLabel}
                      shrink={false}
                    >
                      {t("marathonResults.chooseGender")}
                    </InputLabel>
                  )}
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={gender}
                    onChange={(event) => setGender(event.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "12px",
                          padding: "0 10px",
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "#e3f2fd",
                          },
                        },
                      },
                    }}
                    sx={selectCategory}
                  >
                    <MenuItem
                      value={"false"}
                      sx={{ borderRadius: "10px" }}
                      className={styles.resetMuiDefaultStyle}
                    >
                      {t("marathonResults.female")}
                    </MenuItem>
                    <MenuItem
                      value={"true"}
                      sx={{ borderRadius: "10px" }}
                      className={styles.resetMuiDefaultStyle}
                    >
                      {t("marathonResults.male")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className={styles.categoryFilter}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span className={styles.selectTitle}>
                    {t("marathonResults.category")}
                  </span>
                  <button
                    className={styles.resetSelectedItem}
                    type="button"
                    onClick={() => setChosenDistanceAge("")}
                  >
                    {t("marathonResults.resetFilter")}
                  </button>
                </div>
                <FormControl fullWidth disabled={distance === ""}>
                  {chosenDistanceAge ? null : (
                    <InputLabel
                      id="demo-simple-select-label"
                      sx={inputLabel}
                      shrink={false}
                    >
                      {t("marathonResults.chooseCategory")}
                    </InputLabel>
                  )}
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={chosenDistanceAge}
                    onChange={(event) =>
                      setChosenDistanceAge(event.target.value)
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "12px",
                          padding: "0 10px",
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "#e3f2fd",
                          },
                        },
                      },
                    }}
                    sx={selectCategory}
                  >
                    <MenuItem
                      value={"null"}
                      sx={{ borderRadius: "10px" }}
                      className={styles.resetMuiDefaultStyle}
                    >
                      {t("marathonResults.pwd")}
                    </MenuItem>
                    {distanceAges.map((distanceAge: any) => (
                      <MenuItem
                        key={distanceAge.id}
                        value={distanceAge.id}
                        sx={{ borderRadius: "10px" }}
                        className={styles.resetMuiDefaultStyle}
                      >
                        {`${
                          distanceAge.gender
                            ? t("marathonResults.male")
                            : t("marathonResults.female")
                        } ${t("marathonResults.from")} ${
                          distanceAge.ageFrom
                        } ${t("marathonResults.to")} ${distanceAge.ageTo}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Box>
          </ThemeProvider>
        ) : null}
        {isGetMarathonResultsLoading || isGetMarathonResultsBySearchLoading ? (
          <Box sx={circularProgressWrapperStyles}>
            <CircularProgress />
          </Box>
        ) : marathonResultsCount === 0 || marathonResultsCount === null ? (
          <Box>
            <p className={styles.noRecords}>{t("marathonResults.noRecords")}</p>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              maxHeight: "80vh",
              marginTop: "30px",
              borderRadius: "12px 12px 0 0",
            }}
          >
            <Table
              sx={{ minWidth: 700 }}
              aria-label="customized table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.name")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.surname")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.gender")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.marathonName")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.distance")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.startNumber")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.category")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.placeAmongAll")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    {t("marathonResults.placeInYourCategory")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    Finish
                  </StyledTableCell>
                  <StyledTableCell sx={{ border: "none" }} align="center">
                    Chip Time
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {marathonResults.map((marathonResult) => (
                  <StyledTableRow key={marathonResult.id}>
                    <StyledTableCell
                      align="center"
                      sx={{ ...participantListTableCell }}
                    >
                      {marathonResult.user?.name}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.user?.surname}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.user?.gender
                        ? t("marathonResults.abbreviationMaleGender")
                        : t("marathonResults.abbreviationFemaleGender")}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.marathon?.name}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.application?.distance}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.application?.number}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {marathonResult.application?.distanceAge ||
                        t("marathonResults.pwd")}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {`${marathonResult?.generalPlace}/${marathonResult?.generalCount}`}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {`${marathonResult?.categoryPlace}/${marathonResult?.categoryCount}`}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {/* {dayjs(marathonResult?.gunTime).format("HH:mm:ss")} */}
                      {marathonResult?.gunTime}
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={participantListTableCell}
                    >
                      {/* {dayjs(marathonResult?.chipTime).format("HH:MM:SS")} */}
                      {marathonResult?.chipTime}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {marathonResultsCount === 0 ||
        marathonResultsCount === null ||
        isGetMarathonResultsLoading ? null : (
          <Stack
            spacing={2}
            sx={{
              margin: "30px auto 20px auto",
              maxWidth: "500px",
              alignItems: "center",
            }}
          >
            <Pagination
              count={
                marathonResultsCount
                  ? Math.ceil(marathonResultsCount / pageSize)
                  : 1
              }
              page={page}
              onChange={handleOnPageChange}
              color="primary"
              size="medium"
              variant="outlined"
              shape="rounded"
              sx={{ margin: "0 auto" }}
            />
          </Stack>
        )}
      </div>
    </>
  );
};

export default AccessibleToAllMarathonResults;
